import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl-next';

import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import TabsIcon from '@atlaskit/icon-lab/core/tabs';
import type { NewCoreIconProps } from '@atlaskit/icon/types';

import { EXTENSION_KEY, EXTENSION_TYPE } from '../nativeTabsConstants.ts';
import type { NativeTabsParameters } from '../nativeTabsTypes.ts';

const i18n = defineMessages({
	tabsExtensionTitle: {
		id: 'native-tabs.extension.title.non-final',
		defaultMessage: 'Tabs',
		description: 'Title of the Native Tabs extension shown in the slash-menu / quick-insert menu.',
	},
	tabsExtensionDescription: {
		id: 'native-tabs.extension.description.non-final',
		defaultMessage: 'Organize content with tabs',
		description:
			'Description of the Native Tabs extension shown under the title in the slash-menu.',
	},
});

type NativeTabsIconProps = Pick<NewCoreIconProps, 'label' | 'size'>;
type NativeTabsManifestIconProps = Partial<NativeTabsIconProps>;

const NativeTabsIcon = ({ label = 'Tabs', size }: NativeTabsManifestIconProps) => (
	<TabsIcon label={label} size={size} />
);

type NativeTabsBaseManifest = Omit<ExtensionManifest<NativeTabsParameters>, 'modules'>;

export const buildBaseManifest = (
	intl: IntlShape,
): {
	icon: (props?: NativeTabsManifestIconProps) => React.JSX.Element;
	manifest: NativeTabsBaseManifest;
} => {
	const title = intl.formatMessage(i18n.tabsExtensionTitle);
	const description = intl.formatMessage(i18n.tabsExtensionDescription);

	const Icon = ({ label = title, size }: NativeTabsManifestIconProps = {}) => (
		<NativeTabsIcon label={label} size={size} />
	);

	return {
		icon: Icon,
		manifest: {
			title,
			type: EXTENSION_TYPE,
			key: EXTENSION_KEY,
			description,
			icons: {
				'48': async () => Icon,
			},
		},
	};
};
